import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useMutationLock } from "../hooks/useMutationLock";
import * as attendanceApi from "../api/attendance";
import { formatDisplayDate } from "../utils/dateFormat";
import {
  PDF_BRAND_FOOTER_MARGIN,
  loadPdfBrandAssets,
  pdfBrandDidDrawPage,
} from "../utils/pdfBrand";
import { downloadDailyAttendanceCsv } from "../utils/attendanceCsv";
import "./AdminAttendance.css";

export default function AdminAttendanceSession() {
  const { courseId, sessionId } = useParams();
  const notificationCtx = React.useContext(NotificationContext);
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const { hasPermission, loading: permLoading } = useViewPermission(
    "view_attendance",
  );
  const { hasPermission: canEdit } = useViewPermission("edit_attendance");
  const { busy, run } = useMutationLock();

  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [session, setSession] = useState(null);
  const [marks, setMarks] = useState([]);
  const [savedMarks, setSavedMarks] = useState([]);
  const [counts, setCounts] = useState({ present: 0, absent: 0, total: 0 });
  const [savedCounts, setSavedCounts] = useState({
    present: 0,
    absent: 0,
    total: 0,
  });
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  const recount = (list) => {
    const present = list.filter((m) => m.status === "present").length;
    return {
      present,
      absent: list.length - present,
      total: list.length,
    };
  };

  const load = async () => {
    try {
      setLoading(true);
      const res = await attendanceApi.getSession(sessionId);
      const nextMarks = res.data.marks || [];
      const nextCounts =
        res.data.counts || { present: 0, absent: 0, total: 0 };
      setCourse(res.data.course);
      setSession(res.data.session);
      setMarks(nextMarks);
      setSavedMarks(nextMarks);
      setCounts(nextCounts);
      setSavedCounts(nextCounts);
      setDirty(false);
      setEditing(false);
    } catch (err) {
      console.error("Error loading session:", err);
      setCourse(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasPermission) {
      setLoading(false);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, sessionId]);

  const setMarkStatus = (registrationCourseId, status) => {
    if (!canEdit || !editing) return;
    setMarks((prev) => {
      const next = prev.map((m) =>
        m.registrationCourseId === registrationCourseId
          ? { ...m, status }
          : m,
      );
      setCounts(recount(next));
      const savedById = new Map(
        savedMarks.map((m) => [m.registrationCourseId, m.status]),
      );
      const isDirty =
        next.length !== savedMarks.length ||
        next.some(
          (m) => savedById.get(m.registrationCourseId) !== m.status,
        );
      setDirty(isDirty);
      return next;
    });
  };

  const handleCancelEdit = () => {
    setMarks(savedMarks);
    setCounts(savedCounts);
    setDirty(false);
    setEditing(false);
  };

  const handleExportPdf = async () => {
    if (!course || !session || exporting) return;
    try {
      setExporting(true);
      const doc = new jsPDF({ orientation: "portrait", unit: "mm" });
      const marginX = 14;
      const assets = await loadPdfBrandAssets(doc);
      const dateLabel = formatDisplayDate(session.sessionDate);
      const rows = savedMarks.map((m) => [
        m.name || "—",
        m.status === "present" ? "Present" : "Absent",
      ]);

      let cursorY = assets.headerH + 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.setTextColor(48, 45, 69);
      doc.text("Attendance report", marginX, cursorY);
      cursorY += 6;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(81, 75, 101);
      doc.text(`${course.title} · ${dateLabel}`, marginX, cursorY);
      cursorY += 5;
      doc.setFontSize(10);
      doc.text(
        `Present ${savedCounts.present} · Absent ${savedCounts.absent} · Total ${savedCounts.total}`,
        marginX,
        cursorY,
      );
      cursorY += 8;

      doc.autoTable({
        startY: cursorY,
        head: [["Name", "Status"]],
        body: rows,
        margin: {
          top: assets.headerH + 6,
          left: marginX,
          right: marginX,
          bottom: PDF_BRAND_FOOTER_MARGIN,
        },
        didDrawPage: pdfBrandDidDrawPage(
          doc,
          assets,
          "attendance report",
          marginX,
        ),
        headStyles: { fillColor: [213, 204, 253], textColor: [64, 59, 85] },
        styles: {
          font: "helvetica",
          fontSize: 11,
          textColor: [48, 45, 69],
          cellPadding: 3.5,
        },
        columnStyles: {
          0: { cellWidth: "auto" },
          1: { cellWidth: 36, halign: "left", fontStyle: "bold" },
        },
        didParseCell: (data) => {
          if (data.section !== "body" || data.column.index !== 1) return;
          const value = String(data.cell.raw || "");
          if (value === "Present") {
            data.cell.styles.textColor = [34, 140, 78];
          } else if (value === "Absent") {
            data.cell.styles.textColor = [196, 48, 48];
          }
        },
      });

      const safeCourse = String(course.title || "course")
        .replace(/[^\w-]+/g, "_")
        .replace(/_+/g, "_")
        .slice(0, 40);
      const safeDate = String(session.sessionDate || "date").replace(
        /[^\d-]/g,
        "",
      );
      doc.save(`attendance_${safeCourse}_${safeDate}.pdf`);
    } catch (err) {
      console.error("Attendance PDF export failed:", err);
      notificationCtx.show({
        message: "Failed to export attendance PDF.",
        stat: "error",
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportCsv = () => {
    if (!course || !session || exportingCsv || savedMarks.length === 0) return;
    try {
      setExportingCsv(true);
      downloadDailyAttendanceCsv({
        courseTitle: course.title,
        sessionDate: session.sessionDate,
        marks: savedMarks,
      });
    } catch (err) {
      console.error("Attendance CSV export failed:", err);
      notificationCtx.show({
        message: "Failed to export attendance CSV.",
        stat: "error",
      });
    } finally {
      setExportingCsv(false);
    }
  };

  const handleSave = async () => {
    await run(async () => {
      try {
        const payload = marks.map((m) => ({
          registrationCourseId: m.registrationCourseId,
          status: m.status,
        }));
        const res = await attendanceApi.updateMarks(sessionId, payload);
        const nextCounts = res.data.counts;
        setCounts(nextCounts);
        setSavedCounts(nextCounts);
        setSavedMarks(marks);
        setDirty(false);
        setEditing(false);
        notificationCtx.show({
          message: `Updated — Present ${nextCounts.present}, Absent ${nextCounts.absent}`,
          stat: "success",
        });
      } catch (err) {
        console.error("Update marks failed:", err);
        notificationCtx.show({
          message: err?.response?.data?.message || "Failed to update marks",
          stat: "error",
        });
      }
    });
  };

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  if (!course || !session) {
    return (
      <div>
        <Navbar />
        <div className="att-shell">
          <div className="att-empty">
            <div className="att-empty__title">Session not found</div>
            <div className="att-empty__hint" style={{ marginBottom: 14 }}>
              This attendance record may have been removed.
            </div>
            <Link
              to={`/admin/attendance/${courseId}`}
              className="att-btn att-btn--ghost"
            >
              Back to course
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="att-shell">
        <div className="att-banner">
          <div className="att-banner__row">
            <div>
              <p className="att-banner__crumb">
                <Link to="/admin/attendance">Attendance courses</Link>
                {" / "}
                <Link to={`/admin/attendance/${courseId}`}>{course.title}</Link>
                {" / "}
                Saved record
              </p>
              <h1 className="att-banner__title">
                {course.title} · {formatDisplayDate(session.sessionDate)}
              </h1>
            </div>

            <div className="att-banner__controls">
              <div className="att-banner__control-group">
                <div className="att-banner__control-label">Record summary</div>
                <div className="att-stats att-stats--banner">
                  <div className="att-stat">
                    Present
                    <strong>{counts.present}</strong>
                  </div>
                  <div className="att-stat">
                    Absent
                    <strong>{counts.absent}</strong>
                  </div>
                  <div className="att-stat">
                    Total
                    <strong>{counts.total}</strong>
                  </div>
                </div>
              </div>

              <div className="att-banner__control-group">
                <div className="att-banner__control-label">Actions</div>
                <div className="att-banner__actions">
                  <button
                    type="button"
                    className="att-btn att-btn--lavender"
                    disabled={exporting || exportingCsv || savedMarks.length === 0}
                    onClick={handleExportPdf}
                  >
                    {exporting ? "Exporting…" : "Export PDF"}
                  </button>
                  <button
                    type="button"
                    className="att-btn att-btn--lavender"
                    disabled={exporting || exportingCsv || savedMarks.length === 0}
                    onClick={handleExportCsv}
                  >
                    {exportingCsv ? "Exporting…" : "Export CSV"}
                  </button>
                  {canEdit && !editing ? (
                    <button
                      type="button"
                      className="att-btn att-btn--mint"
                      onClick={() => setEditing(true)}
                    >
                      Enable editing
                    </button>
                  ) : null}
                  {canEdit && editing ? (
                    <>
                      <button
                        type="button"
                        className="att-btn att-btn--ghost"
                        disabled={busy}
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="att-btn att-btn--mint"
                        disabled={!dirty || busy}
                        onClick={handleSave}
                      >
                        {busy ? "Saving…" : "Save changes"}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="att-spacer" />

        <div className="att-table-wrap">
          <div className="att-table-scroll">
            <table className="table align-middle mb-0 att-table">
              <thead>
                <tr>
                  <th>NAME</th>
                  <th>REG ID</th>
                  <th>EMAIL</th>
                  <th>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {marks.map((mark) => (
                  <tr key={mark.registrationCourseId}>
                    <td className="att-table__left" style={{ fontWeight: 600 }}>
                      {mark.name}
                    </td>
                    <td>{mark.registrationId}</td>
                    <td className="att-table__left">{mark.email || "—"}</td>
                    <td>
                      <label
                        className={`att-switch${
                          mark.status === "present" ? " is-on" : " is-off"
                        }${
                          !canEdit || !editing || busy ? " is-disabled" : ""
                        }`}
                      >
                        <span className="att-switch__label" aria-hidden="true">
                          {mark.status === "present" ? "Present" : "Absent"}
                        </span>
                        <input
                          type="checkbox"
                          role="switch"
                          checked={mark.status === "present"}
                          disabled={!canEdit || !editing || busy}
                          aria-label={`Mark ${mark.name} ${
                            mark.status === "present" ? "absent" : "present"
                          }`}
                          onChange={(e) =>
                            setMarkStatus(
                              mark.registrationCourseId,
                              e.target.checked ? "present" : "absent",
                            )
                          }
                        />
                        <span className="att-switch__track" aria-hidden="true">
                          <span className="att-switch__thumb" />
                        </span>
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
